package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.Pomodoro_session
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Repository des sessions Pomodoro (spec POMODORO §3.1, §8.1, §8.11–8.13).
 */
class PomodoroSessionRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Pomodoro_session>> =
        db.pomodoroSessionQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeById(id: String): Flow<Pomodoro_session?> =
        db.pomodoroSessionQueries.selectById(id).asFlow().mapToOneOrNull(Dispatchers.Default)

    fun observeHistory(today: String, limit: Long = 30): Flow<List<Pomodoro_session>> =
        db.pomodoroSessionQueries.selectHistory(today, limit).asFlow().mapToList(Dispatchers.Default)

    /**
     * Récupère ou crée la session du jour (spec §8.1) :
     *   1) Session étendue dont date_start ≤ today ≤ date_end → la retourne.
     *   2) Sinon session avec date_start = today → la retourne.
     *   3) Sinon crée une nouvelle Active à 25 min.
     */
    suspend fun getOrCreateToday(today: String): Pomodoro_session = withContext(Dispatchers.Default) {
        val extended = db.pomodoroSessionQueries.selectExtendedCovering(today, today).executeAsOneOrNull()
        if (extended != null) return@withContext extended

        val exact = db.pomodoroSessionQueries.selectByDate(today).executeAsOneOrNull()
        if (exact != null) return@withContext exact

        val now = nowMillis()
        // L'identifiant découle du jour : deux appareils qui ouvrent la même journée avant de
        // s'être synchronisés créent la même ligne, et leurs tâches se retrouvent dans une
        // seule liste au lieu de deux journées parallèles.
        val id = "pom-day-$today"
        db.pomodoroSessionQueries.insert(
            id = id,
            date_start = today,
            date_end = null,
            status = "active",
            timer_minutes = 25,
            started_at = now,
            ended_at = null,
            created_at = now,
            updated_at = now,
        )
        db.pomodoroSessionQueries.selectById(id).executeAsOne()
    }

    /** Clôture (spec §8.11). No-op si déjà done. */
    suspend fun terminate(id: String) = withContext(Dispatchers.Default) {
        val current = db.pomodoroSessionQueries.selectById(id).executeAsOneOrNull() ?: return@withContext
        if (current.status == "done") return@withContext
        val now = nowMillis()
        db.pomodoroSessionQueries.updateStatus("done", now, now, id)
    }

    /** Réouverture (spec §8.12). Appelée implicitement avant toute mutation sur une session done. */
    suspend fun reopen(id: String) = withContext(Dispatchers.Default) {
        val current = db.pomodoroSessionQueries.selectById(id).executeAsOneOrNull() ?: return@withContext
        if (current.status == "active") return@withContext
        db.pomodoroSessionQueries.updateStatus("active", null, nowMillis(), id)
    }

    suspend fun setTimerMinutes(id: String, minutes: Long) = withContext(Dispatchers.Default) {
        db.pomodoroSessionQueries.updateTimerMinutes(minutes, nowMillis(), id)
    }

    /** Étend (date_end = endDate) ou annule l'extension (endDate = null). Spec §8.9. */
    suspend fun setDateEnd(id: String, endDate: String?) = withContext(Dispatchers.Default) {
        db.pomodoroSessionQueries.updateDateEnd(endDate, nowMillis(), id)
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.pomodoroSessionQueries.softDelete(now, now, id)
    }
}
